import {
  keepPreviousData,
  type QueryFunctionContext,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { api } from '@/api';
import { END_POINT } from '@/constants/end-point.constants';
import {
  DRINK_MATERIAL_QUERY_KEYS,
  PRINTER_AND_FIXTURE_QUERY_KEYS,
} from '@/constants/query-keys.constants';

import type {
  DrinkMaterialItemsResponse,
  DrinkMaterialResponse,
  MaterialsResponse,
  PrinterAndFixtureRequest,
  PrinterAndFixturesResponse,
} from '../types';

const fetchDrinkMaterial = ({
  queryKey: [{ filters, id }],
}: QueryFunctionContext<
  ReturnType<(typeof DRINK_MATERIAL_QUERY_KEYS)['list']>
>): Promise<AxiosResponse<MaterialsResponse>> =>
  api.get(END_POINT.drinkMaterial(id!), { params: filters });

export const useDrinkMaterial = (
  id: string,
  filters?: unknown,
  useQueryOptions?: Partial<UseQueryOptions>,
) => {
  const { enabled = true } = useQueryOptions ?? {};

  return useQuery({
    queryKey: DRINK_MATERIAL_QUERY_KEYS.list(filters, id),
    queryFn: enabled ? fetchDrinkMaterial : skipToken,
    placeholderData: keepPreviousData,
    select: data => data.data,
  });
};

const fetchPrinterAndFixtures = ({
  queryKey: [{ filters, id }],
}: QueryFunctionContext<
  ReturnType<(typeof PRINTER_AND_FIXTURE_QUERY_KEYS)['list']>
>): Promise<AxiosResponse<PrinterAndFixturesResponse>> =>
  api.get(END_POINT.printerAndFixtures(id!), { params: filters });

export const usePrinterAndFixtures = (
  id: string,
  filters?: unknown,
  useQueryOptions?: Partial<UseQueryOptions>,
) => {
  const { enabled = true } = useQueryOptions ?? {};

  return useQuery({
    queryKey: PRINTER_AND_FIXTURE_QUERY_KEYS.list(filters, id),
    queryFn: enabled ? fetchPrinterAndFixtures : skipToken,
    placeholderData: keepPreviousData,
    select: data => data.data,
  });
};

const fetchDrinkMaterialDetail = async (
  id: string,
): Promise<AxiosResponse<DrinkMaterialResponse>> =>
  api.get(END_POINT.drinkMaterialDetail(id));

export const useDrinkMaterialDetail = (id: string) =>
  useQuery({
    queryKey: DRINK_MATERIAL_QUERY_KEYS.detail(id),
    queryFn: () => fetchDrinkMaterialDetail(id),
    select: data => data.data,
  });

const fetchDrinkMaterialItems = async (): Promise<
  AxiosResponse<{ drink_and_materials: DrinkMaterialItemsResponse[] }>
> => api.get(END_POINT.drinkMaterialItems);

export const useDrinkMaterialItems = () =>
  useQuery({
    queryKey: DRINK_MATERIAL_QUERY_KEYS.detail('material-items'),
    queryFn: () => fetchDrinkMaterialItems(),
    select: data => data.data,
  });

const fetchPrinterAndFixtureDetail = async (
  id: string,
): Promise<AxiosResponse<PrinterAndFixtureRequest>> =>
  api.get(END_POINT.printerAndFixturesDetail(id));

export const usePrinterAndFixtureDetail = (id: string) =>
  useQuery({
    queryKey: PRINTER_AND_FIXTURE_QUERY_KEYS.detail(id),
    queryFn: id ? () => fetchPrinterAndFixtureDetail(id) : skipToken,
    select: data => data.data,
  });
